import type { Events, States } from '../common/fsm.js'
import type NavItem from '../nav-item/NavItem.js'
import * as lockIcon from '@nordhealth/icons/lib/assets/navigation-toggle-lock.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { styleMap } from 'lit/directives/style-map.js'
import { BroadcastChannelController } from '../common/controllers/BroadcastChannelController.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { EventController } from '../common/controllers/EventController.js'
import { LightDismissController } from '../common/controllers/LightDismissController.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { fsm } from '../common/fsm.js'
import { clamp } from '../common/number.js'
import { storage } from '../common/storage.js'

import componentStyle from '../common/styles/Component.css'
import stickyStyle from '../common/styles/Sticky.css'

import Icon from '../icon/Icon.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './Layout.css'
import { NavOpenChangeEvent } from './NavOpenChangeEvent.js'
import { NavResizeEvent } from './NavResizeEvent.js'
import '../nav-toggle/NavToggle.js'
import '../tooltip/Tooltip.js'

Icon.registerIcon(lockIcon)

let resizeTooltipIdCounter = 0

const NAV_DEFAULT_WIDTH = 250
const NAV_MIN_WIDTH = 220
const NAV_MAX_WIDTH = 400
const NAV_COLLAPSE_WIDTH = 100
const NAV_RAIL_WIDTH = 64
const NAV_RESIZE_STEP = 30
const NAV_PEEK_DELAY = 300
const NAV_RESIZE_DRAG_THRESHOLD = 4

const mediaQuery
  = typeof matchMedia !== 'undefined'
    ? matchMedia('(min-width: 768px)')
    : ({
        matches: false,
        addEventListener() {
          /* noop */
        },
      } as unknown as MediaQueryList)

const navWidthStore = storage('nord-layout.navWidth', NAV_DEFAULT_WIDTH)
// Tracks the last `default-nav-width` we wrote, so we can re-seed the
// stored width when consumers change the default between loads.
// Initial default matches NAV_DEFAULT_WIDTH so existing consumers that
// never set `default-nav-width` get no re-seed on upgrade — their users'
// persisted widths are preserved.
const navWidthDefaultStore = storage('nord-layout.navWidthDefault', NAV_DEFAULT_WIDTH)
const navOpenStore = storage('nord-layout.navOpen', true)

function findNavToggleInPath(path: EventTarget[]): HTMLElement | null {
  for (const target of path) {
    if (target instanceof HTMLElement && target.localName === 'nord-nav-toggle') {
      return target
    }
  }
  return null
}

function isNavToggleClick(e: Event): boolean {
  return findNavToggleInPath(e.composedPath()) !== null
}

const navMachine = fsm({
  opened: {
    toggle: 'closed',
    close: 'closed',
  },
  closed: {
    toggle: 'opened',
    open: 'opened',
    focusin: 'peek',
    pointerenter: 'peek',
  },
  peek: {
    toggle: 'opened',
    focusout: 'unpeek',
    pointerleave: 'wait',
    dropdownOpen: 'blocked',
    click: 'unpeek',
    open: 'opened',
  },
  blocked: {
    dropdownClose: 'peek',
    open: 'opened',
  },
  wait: {
    toggle: 'opened',
    focusin: 'peek',
    pointerenter: 'peek',
    timeout: 'unpeek',
  },
  unpeek: {
    toggle: 'opened',
    focusin: 'peek',
    pointerenter: 'peek',
    transitionend: 'closed',
  },
})

type NavState = States<typeof navMachine>
type NavEvent = Events<typeof navMachine>

const isNavItem = (el: HTMLElement): el is NavItem => el.localName === 'nord-nav-item'
const isNavItemLeaf = (el: HTMLElement) => isNavItem(el) && !el.hasSubNav

/**
 * Layout component is used to create the main layout of an app. Layout
 * currently comes with one main configuration: two-column.
 *
 * @status ready
 * @category layout
 * @slot - The default main section content.
 * @slot nav - Used to place content inside the navigation sidebar.
 * @slot top-bar - Used to place the [Top Bar](/components/top-bar/) component.
 * @slot header - Used to place content inside the header section. This slot can be made sticky by utilizing the `sticky` property on the layout component.
 * @slot footer - Used to place content inside the footer section. This slot can be made sticky by utilizing the `stickyFooter` property on the layout component.
 * @slot drawer - Used to place additional content/details relating to a selected item.
 * @slot nav-toggle - Used to place a own nav-toggle component, for cases where you might need to add a tooltip. As an alternative, `<nord-nav-toggle>` can be placed anywhere inside `<nord-layout>` (e.g. inside a `<nord-header>`) — the layout listens for clicks and hover on any descendant `<nord-nav-toggle>` and drives toggle / peek behaviour accordingly. When using that placement, set `hide-default-nav-toggle` to suppress the layout's built-in fallback toggle.
 *
 * @fires {NavOpenChangeEvent} nav-open-change - Dispatched when the navigation open state changes, regardless of the trigger (user toggle, peek, focus, programmatic). The event's `navOpen` property contains the new state.
 * @fires {NavResizeEvent} nav-resize - Dispatched when the navigation sidebar width changes. The event's `width` property contains the new width in pixels.
 *
 * @cssprop [--n-layout-padding=var(--n-space-l)] - Controls the padding around the main layout area (the main slot), using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-layout-drawer-inline-size=320px] - Controls the width of the drawer area, when used.
 * @cssprop [--n-layout-background-color=var(--n-color-background)] - Controls the background color of the layout, using [color tokens](/tokens/#color).
 * @cssprop [--n-layout-nav-border-color=var(--n-color-border)] - Controls the color of the border between the navigation and the main content area when the nav is open on wide screens.
 * @cssprop [--n-layout-resize-color=var(--n-color-accent)] - Controls the colour of the resize handle's accent bar (shown on hover, focus, and while dragging).
 * @cssprop [--n-layout-resize-block-size=100%] - Controls the block-axis (vertical) size of the resize handle. Set to a fixed length (e.g. `200px`) to constrain the handle so it doesn't span the full nav height.
 * @cssprop [--n-layout-resize-inset-inline-end=5px] - Controls the inline-end offset of the resize handle's accent bar (the visible vertical line shown on hover/focus/drag).
 * @cssprop [--n-layout-nav-rail-width=64px] - Controls the width of the navigation column when `collapse-mode="rail"` is active and the nav is closed. Equivalent to the `nav-rail-width` attribute / `navRailWidth` property; the CSS custom property takes precedence when set.
 * @cssprop [--n-layout-header-size=var(--n-space-xxl)] - Controls the block-axis (height) of the layout's header area. Also drives the size of the nav-toggle container and sticky-positioning offsets.
 * @cssprop [--n-layout-drawer-box-shadow=var(--n-box-shadow-nav)] - Controls the box-shadow applied to the drawer (aside) on narrow viewports.
 * @cssprop [--n-layout-drawer-box-shadow-wide=var(--n-box-shadow-header)] - Controls the box-shadow applied to the drawer (aside) on wide viewports (≥1240px).
 * @cssprop [--n-layout-drawer-border-inline-start-width=1px] - Controls the inline-start border width of the drawer on wide viewports.
 * @cssprop [--n-layout-drawer-border-inline-start-color=var(--n-color-border)] - Controls the inline-start border color of the drawer on wide viewports.
 *
 * @localization resizeNavigation - Accessible label for the nav resize separator.
 * @localization navigationCollapsed - Announced by the nav resize separator (`aria-valuetext`) while the navigation is collapsed.
 */
@customElement('nord-layout')
export default class Layout extends LitElement {
  static styles = [componentStyle, stickyStyle, style]

  private peekTimeoutId?: ReturnType<typeof setTimeout>
  private readonly resizeTooltipId = `n-layout-resize-tip-${++resizeTooltipIdCounter}`

  private navSlot = new SlotController(this, 'nav')
  private drawerSlot = new SlotController(this, 'drawer')
  private topBarSlot = new SlotController(this, 'top-bar')
  private headerSlot = new SlotController(this, 'header')
  private footerSlot = new SlotController(this, 'footer')
  private dividerTooltipSlot = new SlotController(this, 'tooltip-divider')
  private direction = new DirectionController(this)
  private events = new EventController(this)
  private localization = new LocalizeController<'nord-layout'>(this)
  private lightDismiss = new LightDismissController(this, {
    isOpen: () => this.navState === 'opened' && !this.wideScreen,
    onDismiss: () => this.navTransition('close'),
    isDismissible: node => node !== this.navEl,
  })

  // Set while applying an incoming BroadcastChannel message. The navOpen
  // observer checks this and skips re-broadcasting so that received changes
  // don't echo back across tabs. Without this gate the two tabs ping-pong
  // on Safari, where microtask scheduling lets their updates interleave
  // past Lit's property equality check.
  private applyingRemote = false

  private broadcast = new BroadcastChannelController<{ navOpen?: boolean, navWidth?: number }>(this, {
    channelName: 'nord-layout-nav-state',
    enabled: () => this.syncNavState && this.wideScreen,
    // Merge messages buffered while the tab is hidden so the latest value of
    // each field survives coalescing — a navOpen update must not be dropped by
    // a later navWidth update, and vice versa.
    coalesce: (pending, incoming) => ({ ...pending, ...incoming }),
    onMessage: (data) => {
      this.applyingRemote = true
      // Value-based echo suppression. Applying a value equal to the current one
      // is a wasted reflow, and on Safari the async `applyingRemote` reset can
      // race such that an equal value echoes back and sustains a loop. Skipping
      // unchanged values makes suppression hold regardless of that timing, and
      // is the equality guard the downstream disable (VCF-2146) asked for.
      if (data.navOpen !== undefined && data.navOpen !== this.navOpen) {
        this.navOpen = data.navOpen
      }
      if (data.navWidth !== undefined && data.navWidth !== this.navWidth) {
        this.navWidth = data.navWidth
      }
      this.updateComplete.then(() => {
        this.applyingRemote = false
      })
    },
  })

  @query('.n-layout-nav', true) private navEl!: HTMLDivElement
  @query('.n-resize-tooltip') private resizeTooltipEl?: HTMLElement
  @query('.n-sticky') private stickyHeaderEl?: HTMLElement
  @query('slot[name="footer"]') private footerSlotEl?: HTMLSlotElement

  // Track the actual rendered block-size of header/footer so consumers
  // (e.g. <nord-aside>) can offset overlay positioning against the real
  // values, not just the static defaults. Single ResizeObserver per
  // dimension, written as public CSS custom properties on the host.
  private headerSizeObserver?: ResizeObserver
  private footerSizeObserver?: ResizeObserver
  private observedFooterElement?: Element

  @state() private navWidth = navWidthStore.value
  @state() private isDragging = false
  @state() private navState!: NavState
  @state() private wideScreen = mediaQuery.matches

  /**
   * Controls whether the navigation is hidden off-screen or not.
   * Defaults to `true` for wide viewports, and `false` otherwise.
   */
  @property({ reflect: true, type: Boolean, attribute: 'nav-open' }) navOpen!: boolean

  /**
   * Controls whether the navigation's open/closed state is persisted across page loads.
   * This is useful for multi-page apps, where clicks on links trigger a full page load.
   */
  @property({ reflect: true, type: Boolean, attribute: 'persist-nav-state' }) persistNavState = false

  /**
   * Controls whether the navigation's open/closed state and width is synced across tabs/windows on the same origin.
   * This is useful for long-lived app sessions where you may have multiple tabs/windows open at once.
   */
  @property({ type: Boolean, reflect: true, attribute: 'sync-nav-state' }) syncNavState = false

  /**
   * Initial width (in pixels) of the navigation sidebar when no value has been persisted.
   * Changing this value between loads also re-seeds the persisted width for existing users,
   * so design changes propagate without manual localStorage resets.
   */
  @property({ type: Number, reflect: true, attribute: 'default-nav-width' }) defaultNavWidth = NAV_DEFAULT_WIDTH

  /**
   * Minimum width (in pixels) the navigation sidebar can be resized to via drag or keyboard.
   */
  @property({ type: Number, reflect: true, attribute: 'min-nav-width' }) minNavWidth = NAV_MIN_WIDTH

  /**
   * Maximum width (in pixels) the navigation sidebar can be resized to via drag or keyboard.
   */
  @property({ type: Number, reflect: true, attribute: 'max-nav-width' }) maxNavWidth = NAV_MAX_WIDTH

  /**
   * Width (in pixels) of the rail column when `collapse-mode="rail"` is active
   * and the nav is closed. Default 64.
   */
  @property({ type: Number, reflect: true, attribute: 'nav-rail-width' }) navRailWidth = NAV_RAIL_WIDTH

  /**
   * Controls the padding of the default main section slot. When set to “none”,
   * the nav and header slots will still have padding.
   */
  @property({ reflect: true }) padding: 'm' | 'none' = 'm'

  /**
   * Controls whether the layout's header has sticky positioning.
   */
  @property({ type: Boolean, reflect: true }) sticky: boolean = false

  /**
   * Controls whether the layout's footer has sticky positioning.
   */
  @property({ type: Boolean, reflect: true, attribute: 'sticky-footer' }) stickyFooter: boolean = false

  /**
   * Suppresses the built-in `<nord-nav-toggle>` rendered as the fallback for
   * the `nav-toggle` slot. Set this when you've placed your own
   * `<nord-nav-toggle>` elsewhere inside the layout (e.g. inside
   * `<nord-header>`) and want the layout's default one hidden.
   */
  @property({ type: Boolean, reflect: true, attribute: 'hide-default-nav-toggle' })
  hideDefaultNavToggle: boolean = false

  /**
   * Suppresses the built-in collapse button rendered inside the nav (the
   * lock-icon button that toggles the sidebar between opened and the
   * collapsed state defined by `collapse-mode`). Set this when you've
   * placed your own collapse control elsewhere.
   */
  @property({ type: Boolean, reflect: true, attribute: 'hide-collapse-button' })
  hideCollapseButton: boolean = false

  /**
   * When set, clicking the resize handle (without dragging) toggles the
   * nav between opened and the collapsed state defined by `collapse-mode`.
   * Dragging the handle still resizes the sidebar as usual; the click
   * behaviour only fires when the pointer barely moves between down and up.
   */
  @property({ type: Boolean, reflect: true, attribute: 'toggle-on-resize-click' })
  toggleOnResizeClick: boolean = false

  /**
   * Controls how the navigation renders when closed on wide viewports.
   * `hide` (default) slides the nav off-screen entirely. `rail` keeps a
   * narrow icon-only column visible, with tooltips for labels and flyout
   * popovers for items that have a `subnav` slot.
   *
   * Narrow viewports always use the off-screen behaviour regardless of
   * this setting.
   */
  @property({ reflect: true, attribute: 'collapse-mode' }) collapseMode: 'hide' | 'rail' = 'hide'

  /**
   * A getter whose values reflects whether the layout component considers the viewport to be narrow or not.
   * A narrow viewport is considered to be less than 768px wide.
   */
  get isNarrow(): boolean {
    return !this.wideScreen
  }

  connectedCallback(): void {
    super.connectedCallback()
    this.events.listen(mediaQuery, 'change', this.handleMediaQueryChange)
    this.addEventListener('click', this.handleHostClick)
    this.addEventListener('mouseover', this.handleHostMouseOver)
    this.addEventListener('mouseout', this.handleHostMouseOut)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener('click', this.handleHostClick)
    this.removeEventListener('mouseover', this.handleHostMouseOver)
    this.removeEventListener('mouseout', this.handleHostMouseOut)
    this.headerSizeObserver?.disconnect()
    this.headerSizeObserver = undefined
    this.footerSizeObserver?.disconnect()
    this.footerSizeObserver = undefined
    this.observedFooterElement = undefined
    this.style.removeProperty('--n-layout-header-block-size')
    this.style.removeProperty('--n-layout-footer-block-size')
  }

  protected firstUpdated() {
    if (typeof ResizeObserver === 'undefined')
      return
    if (this.stickyHeaderEl) {
      this.headerSizeObserver = new ResizeObserver(() => this.publishHeaderSize())
      this.headerSizeObserver.observe(this.stickyHeaderEl)
      this.publishHeaderSize()
    }
    if (this.footerSlotEl) {
      this.footerSlotEl.addEventListener('slotchange', this.handleFooterSlotChange)
      this.handleFooterSlotChange()
    }
  }

  private publishHeaderSize() {
    const size = this.stickyHeaderEl?.getBoundingClientRect().height ?? 0
    if (size > 0)
      this.style.setProperty('--n-layout-header-block-size', `${size}px`)
    else
      this.style.removeProperty('--n-layout-header-block-size')
  }

  private handleFooterSlotChange = () => {
    const next = this.footerSlotEl?.assignedElements({ flatten: true })[0]
    if (next === this.observedFooterElement)
      return
    this.footerSizeObserver?.disconnect()
    this.observedFooterElement = next
    if (!next) {
      this.style.removeProperty('--n-layout-footer-block-size')
      return
    }
    this.footerSizeObserver = new ResizeObserver(() => this.publishFooterSize())
    this.footerSizeObserver.observe(next)
    this.publishFooterSize()
  }

  private publishFooterSize() {
    const size = this.observedFooterElement?.getBoundingClientRect().height ?? 0
    if (size > 0)
      this.style.setProperty('--n-layout-footer-block-size', `${size}px`)
    else
      this.style.removeProperty('--n-layout-footer-block-size')
  }

  willUpdate() {
    // we only want this logic to run once, before first render
    if (this.hasUpdated) {
      return
    }

    // fall back to the built-in constants when consumer-provided attributes
    // are non-numeric (e.g. `min-nav-width="abc"` produces NaN). reflect:true
    // also rewrites the attribute in the DOM to the sanitized value
    if (!Number.isFinite(this.defaultNavWidth)) {
      this.defaultNavWidth = NAV_DEFAULT_WIDTH
    }
    if (!Number.isFinite(this.minNavWidth)) {
      this.minNavWidth = NAV_MIN_WIDTH
    }
    if (!Number.isFinite(this.maxNavWidth)) {
      this.maxNavWidth = NAV_MAX_WIDTH
    }

    // re-seed persisted width when the consumer-provided default-nav-width
    // differs from the last default we applied. This lets apps roll out a
    // new sidebar default without manual localStorage migrations.
    if (navWidthDefaultStore.value !== this.defaultNavWidth) {
      this.setNavWidth(this.defaultNavWidth)
      navWidthStore.value = this.navWidth
      // store the raw consumer-provided default so mismatch detection is
      // independent of the min/max clamp
      navWidthDefaultStore.value = this.defaultNavWidth
    }
    else {
      // ensure a previously-persisted width still respects current min/max
      this.setNavWidth(this.navWidth)
    }

    // on mobile it should always start closed
    if (this.isNarrow) {
      this.navState = 'closed'
    }
    // if nav state is persisted, and it was previously closed, start closed
    else if (this.persistNavState && navOpenStore.value === false) {
      this.navState = 'closed'
    }
    // otherwise start open
    else {
      this.navState = 'opened'
    }
  }

  render() {
    const { navWidth, navState, navSlot, headerSlot, footerSlot, topBarSlot, drawerSlot, isDragging, direction } = this
    const isRailCollapsed = this.wideScreen && this.collapseMode === 'rail' && navState !== 'opened'
    const adjustedNavWidth = navState === 'opened' && this.wideScreen
      ? navWidth
      : isRailCollapsed
        ? NAV_RAIL_WIDTH
        : this.defaultNavWidth

    return html`
      <div
        class=${classMap({
          'n-layout': true,
          'n-rtl': direction.isRTL,
          'n-dragging': isDragging,
          'n-has-header': headerSlot.hasContent,
          'n-has-footer': footerSlot.hasContent,
          'n-has-top-bar': topBarSlot.hasContent,
          'n-has-drawer': drawerSlot.hasContent,
          'n-has-nav': navSlot.hasContent,
        })}
        style=${styleMap({
          '--_n-layout-nav-width': `${adjustedNavWidth}px`,
          '--_n-layout-nav-rail-width': `var(--n-layout-nav-rail-width, ${this.navRailWidth}px)`,
          // Mirror the min/max nav-width props as px so the hide-mode peek
          // flyout can clamp its intrinsic (`fit-content`) width in CSS.
          '--_n-layout-nav-min-width': `${this.minNavWidth}px`,
          '--_n-layout-nav-max-width': `${this.maxNavWidth}px`,
        })}
        data-nav=${navSlot.hasContent ? navState : 'closed'}
        data-screen=${this.wideScreen ? 'wide' : 'narrow'}
        data-collapse-mode=${this.collapseMode}
      >
        <div
          id="n-layout-nav"
          class="n-layout-nav"
          ?hidden=${navSlot.isEmpty}
          @focusin=${this.handleNavFocus}
          @mouseenter=${this.handleMouseEnter}
          @mouseleave=${this.handleMouseLeave}
          @open=${this.handleDropdownOpen}
          @close=${this.handleDropdownClose}
        >
          <slot name="nav" @click=${this.handleNavClick}></slot>
          <div
            class="n-resize"
            role="separator"
            aria-orientation="vertical"
            tabindex="0"
            aria-label=${this.localization.term('resizeNavigation')}
            aria-controls="n-layout-nav"
            aria-valuemin=${this.minNavWidth}
            aria-valuemax=${this.maxNavWidth}
            aria-valuenow=${this.resizeValueNow}
            aria-valuetext=${cond(navState !== 'opened', this.localization.term('navigationCollapsed'))}
            aria-describedby=${cond(this.dividerTooltipSlot.hasContent && !isDragging, this.resizeTooltipId)}
            @pointerdown=${cond(navState === 'opened' || isRailCollapsed, this.startDragging)}
            @pointermove=${isDragging ? this.handleDrag : this.handleResizeHoverMove}
            @pointerleave=${this.handleResizePointerLeave}
            @pointerup=${this.stopDragging}
            @keydown=${this.handleKeyboardResize}
            ?hidden=${this.isNarrow}
          ></div>
          ${this.dividerTooltipSlot.hasContent
            ? html`
                <nord-tooltip id=${this.resizeTooltipId} position="inline-end" class="n-resize-tooltip">
                  <slot name="tooltip-divider"></slot>
                </nord-tooltip>
              `
            : nothing}
          ${this.hideCollapseButton ? nothing : this.renderNavCollapse()}
        </div>

        <div class="n-layout-main" @focusin=${this.handleMainFocus} @click=${this.handleMainClick}>
          ${navSlot.hasContent && !this.hideDefaultNavToggle ? this.renderNavToggle() : nothing}

          <div class="n-top-bar-container">
            <slot name="top-bar"></slot>
          </div>

          <div class="n-layout-content">
            <div class="n-sticky">
              <slot name="header"></slot>
            </div>
            <main>
              <slot></slot>
            </main>
            <slot name="footer"></slot>
          </div>

          <aside ?hidden=${this.drawerSlot.isEmpty}>
            <slot name="drawer"></slot>
          </aside>
        </div>
      </div>
    `
  }

  private renderNavToggle() {
    return html`
      <div class="n-nav-toggle-container">
        <slot
          name="nav-toggle"
          @click=${this.handleToggleClick}
          @mouseover=${this.handleMouseEnter}
          @mouseout=${this.handleMouseLeave}
        >
          <nord-nav-toggle></nord-nav-toggle>
        </slot>
      </div>
    `
  }

  private renderNavCollapse() {
    return html`
      <button class="n-collapse-btn" @click=${this.handleToggleClick}>
        <nord-icon name="navigation-toggle-lock" size="s"></nord-icon>
      </button>
    `
  }

  @observe('navWidth', 'updated')
  protected handleNavWidthChange(prev: number) {
    navWidthStore.value = this.navWidth
    if (prev != null) {
      const navWidth = this.navWidth
      this.dispatchEvent(new NavResizeEvent(navWidth))
    }
  }

  @observe('navState')
  protected handleNavStateChange(prev: NavState) {
    if (prev === 'wait' && this.peekTimeoutId) {
      clearTimeout(this.peekTimeoutId)
    }
    if (prev === 'unpeek') {
      this.navEl.removeEventListener('transitionend', this.handleTransitionEnd)
    }

    switch (this.navState) {
      case 'closed':
        this.navOpen = false
        break
      case 'opened':
        this.navOpen = true
        break
      case 'wait':
        this.peekTimeoutId = setTimeout(() => this.navTransition('timeout'), NAV_PEEK_DELAY)
        break
      case 'unpeek':
        this.navEl.addEventListener('transitionend', this.handleTransitionEnd, { once: true })
        break
      default:
        break
    }
  }

  @observe('navOpen', 'updated')
  protected handleOpenChange(prev: boolean) {
    if (!this.isDragging) {
      // when opening nav, it should restore to default width (or larger).
      // unless it is being dragged, in which case the drag width wins
      this.setNavWidth(Math.max(this.navWidth, this.defaultNavWidth))
    }

    this.navTransition(this.navOpen ? 'open' : 'close')

    if (this.persistNavState) {
      navOpenStore.value = this.navOpen
    }

    // skip first update, we only want subsequent changes
    if (prev != null) {
      // Bind to a local so the custom-elements-manifest analyzer can infer
      // the event name from this constructor call site (it reads the bare
      // identifier passed to `new NavOpenChangeEvent(...)`; a `this.x`
      // member access defeats that heuristic and leaves the event nameless,
      // which then crashes `scripts/build-react-types.mjs` on sort).
      const navOpen = this.navOpen
      // Suppress the broadcast when the change came from a received message,
      // otherwise tabs would echo every update back at each other. See the
      // `applyingRemote` field for the full rationale.
      if (!this.applyingRemote) {
        this.broadcast.post({ navOpen })
      }
      this.dispatchEvent(new NavOpenChangeEvent(navOpen))
    }
  }

  /* ---------------------------------------------
  / NAVIGATION OPEN/CLOSE LOGIC
  / --------------------------------------------- */

  private navTransition(event: NavEvent) {
    this.navState = navMachine.transition(this.navState, event)
  }

  private handleNavClick(e: Event) {
    if (!e.defaultPrevented && !this.wideScreen) {
      const target = e.target as HTMLElement

      if (isNavItemLeaf(target)) {
        this.navTransition('close')
      }
    }
  }

  private handleMainClick() {
    this.navTransition('click')
  }

  private handleDropdownOpen(e: Event) {
    const target = e.target as Element

    if (target.localName === 'nord-dropdown') {
      this.navTransition('dropdownOpen')
    }
  }

  private handleDropdownClose(e: Event) {
    const target = e.target as Element

    if (target.localName === 'nord-dropdown') {
      this.navTransition('dropdownClose')
    }
  }

  private handleMediaQueryChange = () => {
    this.wideScreen = mediaQuery.matches
    this.navTransition(this.wideScreen ? 'open' : 'close')
  }

  private handleToggleClick = (e: Event) => {
    // Skip when a `<nord-nav-toggle>` is in the click path — `handleHostClick`
    // will fire for it. Without this guard, an in-slot `<nord-nav-toggle>`
    // would toggle twice (once via the slot handler, once via the host one).
    if (isNavToggleClick(e)) {
      return
    }
    this.navTransition('toggle')
  }

  // Allow `<nord-nav-toggle>` to be placed anywhere inside the layout, not
  // just in the `nav-toggle` slot. Clicks on the host bubble up through the
  // light-DOM subtree; if the composed path contains a `<nord-nav-toggle>`,
  // we treat that as a toggle request.
  private handleHostClick = (e: Event) => {
    if (isNavToggleClick(e)) {
      this.navTransition('toggle')
    }
  }

  // Same delegation pattern as `handleHostClick`, but for hover-to-peek.
  // `relatedTarget` filtering avoids re-firing as the cursor moves between
  // children inside the same `<nord-nav-toggle>`.
  private handleHostMouseOver = (e: MouseEvent) => {
    const toggle = findNavToggleInPath(e.composedPath())
    if (!toggle) {
      return
    }
    if (e.relatedTarget instanceof Node && toggle.contains(e.relatedTarget)) {
      return
    }
    this.handleMouseEnter()
  }

  private handleHostMouseOut = (e: MouseEvent) => {
    const toggle = findNavToggleInPath(e.composedPath())
    if (!toggle) {
      return
    }
    if (e.relatedTarget instanceof Node && toggle.contains(e.relatedTarget)) {
      return
    }
    this.handleMouseLeave()
  }

  private handleNavFocus = () => {
    // In rail mode, the icons are already visible — focus inside the
    // rail should not trigger a peek that re-expands the full sidebar.
    if (this.wideScreen && this.collapseMode === 'rail') {
      return
    }
    this.navTransition('focusin')
  }

  private handleMainFocus = () => {
    this.navTransition('focusout')
  }

  private handleMouseEnter = () => {
    if (!this.wideScreen) {
      return
    }
    if (this.collapseMode === 'rail') {
      return
    }
    this.navTransition('pointerenter')
  }

  private handleMouseLeave = () => {
    if (this.wideScreen && this.collapseMode === 'rail') {
      return
    }
    this.navTransition('pointerleave')
  }

  private handleTransitionEnd = () => {
    this.navTransition('transitionend')
  }

  /* ---------------------------------------------
  / RESIZE LOGIC
  / --------------------------------------------- */

  /**
   * `aria-valuenow` for the focusable resize separator (ARIA window splitter
   * pattern): the current nav width in pixels, bounded by the `min-nav-width`
   * / `max-nav-width` props exposed as `aria-valuemin` / `aria-valuemax`.
   * While the nav is collapsed the value must stay within those bounds, so it
   * reports the minimum and `aria-valuetext` announces the collapsed state.
   */
  private get resizeValueNow(): number {
    if (this.navState !== 'opened') {
      return this.minNavWidth
    }
    return clamp(Math.round(this.navWidth), this.minNavWidth, this.maxNavWidth)
  }

  private handleKeyboardResize(e: KeyboardEvent) {
    const {
      navWidth,
      direction: { isLTR },
    } = this

    switch (e.key) {
      case 'ArrowLeft':
        this.setNavWidth(navWidth + (isLTR ? -NAV_RESIZE_STEP : NAV_RESIZE_STEP))
        break
      case 'ArrowRight':
        this.setNavWidth(navWidth + (isLTR ? NAV_RESIZE_STEP : -NAV_RESIZE_STEP))
        break
      case 'Enter':
        this.navTransition('toggle')
        break
      case 'Home':
        this.setNavWidth(this.minNavWidth)
        break
      case 'End':
        this.setNavWidth(this.maxNavWidth)
        break
      default:
        return
    }

    this.broadcast.post({ navWidth: this.navWidth })
    e.preventDefault()
  }

  private setNavWidth(width: number) {
    this.navWidth = clamp(Math.round(width), this.minNavWidth, this.maxNavWidth)
  }

  private dragStartX = 0
  private hasDragged = false

  private startDragging(e: PointerEvent) {
    if (e.button === 0) {
      const target = e.target as Element
      target.setPointerCapture(e.pointerId)
      this.isDragging = true
      this.dragStartX = e.clientX
      this.hasDragged = false
    }
  }

  private stopDragging() {
    if (this.isDragging && this.toggleOnResizeClick && !this.hasDragged) {
      this.navTransition('toggle')
    }
    this.isDragging = false
    this.hasDragged = false
    this.broadcast.post({ navWidth: this.navWidth })
  }

  private handleResizeHoverMove = (e: PointerEvent) => {
    const target = e.currentTarget as HTMLElement | null
    if (!target || !this.resizeTooltipEl)
      return

    const rect = target.getBoundingClientRect()
    const offset = e.clientY - (rect.top + rect.height / 2)
    this.resizeTooltipEl.style.setProperty('--n-resize-tooltip-y', `${offset}px`)
  }

  private handleResizePointerLeave = () => {
    this.stopDragging()
    this.resizeTooltipEl?.style.removeProperty('--n-resize-tooltip-y')
  }

  private handleDrag(e: PointerEvent) {
    if (!this.hasDragged && Math.abs(e.clientX - this.dragStartX) < NAV_RESIZE_DRAG_THRESHOLD) {
      return
    }
    this.hasDragged = true

    const width = this.direction.isRTL ? this.clientWidth - e.clientX : e.clientX

    this.setNavWidth(width)
    this.navTransition(width >= NAV_COLLAPSE_WIDTH ? 'open' : 'close')
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-layout': Layout
  }
}
